import { TFunction } from 'i18next';
import { buildMonitorLink } from '@Helpers';

import { ClientConfiguration, DashboardGroup, DashboardItem } from '@Models';
import { SidebarIcons } from '../icons';
import { MenuItem } from '../types';

/**
 * Builds the Monitor section: direct link when one dashboard, submenu when
 * multiple, with one level of nesting for groups.
 *
 * Mirrors the legacy IIFE in sidebar.container.tsx but returns typed
 * MenuItem[] instead of the old `(MenuItem | null)[]` shape.
 */
const buildDashboardItems = (dashboards: ClientConfiguration['dashboards'] | undefined, t: TFunction): MenuItem[] => {
  // Monitor is always visible — MonitorContainer renders the empty/provisioning
  // states when there is nothing to show.
  if (!dashboards || dashboards.length === 0) {
    return [
      {
        key: 'monitor-empty',
        label: t('Sidebar.Monitor'),
        icon: SidebarIcons.dashboard,
        link: '/monitor',
        enabled: true,
      },
    ];
  }

  const allDashboards = dashboards.flatMap((item) => ('group' in item ? item.dashboards : [item as DashboardItem]));

  if (allDashboards.length === 1) {
    return [
      {
        key: 'monitor-single',
        label: t('Sidebar.Monitor'),
        icon: SidebarIcons.dashboard,
        link: buildMonitorLink(allDashboards[0]),
        enabled: true,
      },
    ];
  }

  const hasOneGroup = dashboards.length === 1 && 'group' in dashboards[0];

  const submenu: MenuItem[] = dashboards.map((item) => {
    if ('group' in item) {
      const group = item as DashboardGroup;
      return {
        key: `group-${group.group}`,
        label: group.group,
        icon: SidebarIcons.dashboard,
        defaultOpen: hasOneGroup,
        enabled: true,
        submenu: group.dashboards.map((d) => ({
          key: `dashboard-${d.id}`,
          label: d.name,
          icon: SidebarIcons.dashboard,
          link: buildMonitorLink(d),
          enabled: true,
        })),
      };
    }
    const dashboard = item as DashboardItem;
    return {
      key: `dashboard-${dashboard.id}`,
      label: dashboard.name,
      icon: SidebarIcons.dashboard,
      link: buildMonitorLink(dashboard),
      enabled: true,
    };
  });

  return [
    {
      key: 'monitor',
      label: t('Sidebar.Monitor'),
      icon: SidebarIcons.dashboard,
      defaultOpen: true,
      submenu,
    },
  ];
};

export default buildDashboardItems;
